/**
 * <BuildUpdateNotice> — an open tab says that a newer build is ready, and reloads only at a safe moment
 * (WEB-APP-VERSION-REFRESH-001).
 *
 * It starts the page's build-freshness monitor and reads the page's reload-safety gate (both `@puffin-core/api-client`).
 * It lives in auth-shell because BOTH shells mount it — page-shell's `<AppShell>` and this package's `<AuthLayout>` (a
 * sign-in screen can stand open on a register for days) — and page-shell depends on auth-shell. Owner ruling Q1:
 *
 * | the tab knows                    | it shows                         | it reloads by itself                       |
 * | -------------------------------- | -------------------------------- | ------------------------------------------ |
 * | a newer build is deployed        | "A new version is ready."        | at the next safe moment, once 30 minutes   |
 * |                                  |                                  | have passed since it first learned         |
 * | a server refused this build      | "This version is out of date."   | at the next safe moment, without waiting   |
 *
 * While the gate is open it offers "Reload now"; while it is shut it says what it waits for (this sale, unsaved
 * changes, a request in flight) and offers nothing that could interrupt it. A polite `role="status"`, never an alert:
 * the news is never urgent enough to interrupt a person mid-task.
 *
 * @module @puffin-core/auth-shell/components/BuildUpdateNotice
 */

import { useEffect, useState, useSyncExternalStore } from 'react';
import PropTypes from 'prop-types';
import {
  BUILD_FRESHNESS_STATUS,
  RELOAD_BLOCK_REASON,
  getPageBuildFreshnessMonitor,
  getPageReloadSafetyGate,
} from '@puffin-core/api-client';
import { InlineFeedback } from '@puffin-core/feedback-ui';

import { useAuthShellTranslate } from '../hooks/useAuthShellTranslate.js';
import { useBandHeightProperty } from '../hooks/useBandHeightProperty.js';

/** How long a tab may keep running an older build before it reloads at the next safe moment (owner ruling Q1). */
export const BUILD_UPDATE_GRACE_MS = 30 * 60 * 1000;

/**
 * What the notice says it waits for, per reason — explicit keys, so the catalog census can see every one.
 *
 * @param {Function} t
 * @returns {Object<string, string>}
 */
function waitingCopy(t) {
  return {
    [RELOAD_BLOCK_REASON.UNSAVED_CHANGES]: t('authShell.buildUpdateWaitingUnsavedChanges'),
    [RELOAD_BLOCK_REASON.SALE_IN_PROGRESS]: t('authShell.buildUpdateWaitingSaleInProgress'),
    [RELOAD_BLOCK_REASON.SIGN_IN_IN_PROGRESS]: t('authShell.buildUpdateWaitingSignInInProgress'),
    [RELOAD_BLOCK_REASON.REQUEST_IN_FLIGHT]: t('authShell.buildUpdateWaitingRequestInFlight'),
  };
}

/**
 * @param {Object} props
 * @param {Object} [props.monitor] - the page's build-freshness monitor (`getPageBuildFreshnessMonitor`)
 * @param {Object} [props.gate] - the page's reload-safety gate (`getPageReloadSafetyGate`)
 * @param {() => number} [props.now] - the clock (ms), shared with the monitor's
 * @param {number} [props.graceMs] - how long a stale tab waits before reloading by itself
 * @param {Function} [props.t] - optional translator; the `authShell` namespace, then English, otherwise
 * @returns {React.JSX.Element|null}
 */
export function BuildUpdateNotice({
  monitor = getPageBuildFreshnessMonitor(),
  gate = getPageReloadSafetyGate(),
  now = Date.now,
  graceMs = BUILD_UPDATE_GRACE_MS,
  t: consumerT,
}) {
  // The third argument is REQUIRED on a hydrated root (every prerendered marketing page): without a server snapshot
  // React throws during hydration and discards the whole server-rendered page. The server never renders the band,
  // and at load the monitor says CURRENT, so the hydrated first render is `null` either way.
  const freshness = useSyncExternalStore(monitor.subscribe, monitor.getState, monitor.getState);
  const safety = useSyncExternalStore(gate.subscribe, gate.getState, gate.getState);
  const [graceElapsedAt, setGraceElapsedAt] = useState(null);

  useEffect(() => {
    monitor.start();
    return () => monitor.stop();
  }, [monitor]);

  const { status, detectedAt } = freshness;
  useEffect(() => {
    if (status !== BUILD_FRESHNESS_STATUS.STALE) return undefined;
    // Always a timer, even when the grace has already passed: the state is set from a callback, never in the effect.
    const timer = setTimeout(
      () => setGraceElapsedAt(detectedAt),
      Math.max(0, detectedAt + graceMs - now()),
    );
    return () => clearTimeout(timer);
  }, [status, detectedAt, graceMs, now]);

  const reloadIsDue =
    status === BUILD_FRESHNESS_STATUS.REQUIRED ||
    (status === BUILD_FRESHNESS_STATUS.STALE && graceElapsedAt === detectedAt);
  const shouldReloadNow = reloadIsDue && safety.open;
  useEffect(() => {
    if (shouldReloadNow) gate.reloadWhenSafe();
  }, [shouldReloadNow, gate]);

  const t = useAuthShellTranslate(consumerT);
  const bandRef = useBandHeightProperty();

  if (status === BUILD_FRESHNESS_STATUS.CURRENT) return null;

  const required = status === BUILD_FRESHNESS_STATUS.REQUIRED;
  // The band IS the one feedback line (SAVE-FEEDBACK-CANON-001 P3; `web/no-raw-alert-markup`): a polite
  // `role="status"` by its tone, the band's own class appended, its height measured off the root through `ref`.
  return (
    <InlineFeedback
      ref={bandRef}
      tone={required ? 'warning' : 'info'}
      className="build-update-band"
      message={required ? t('authShell.buildUpdateRequired') : t('authShell.buildUpdateReady')}
      actions={
        safety.open
          ? [
              {
                key: 'reload',
                label: t('authShell.buildUpdateReloadNow'),
                onClick: () => gate.reloadWhenSafe(),
              },
            ]
          : []
      }
      testId="build-update-notice"
    >
      {safety.open ? null : <p className="alert-message">{waitingCopy(t)[safety.blockers[0]]}</p>}
    </InlineFeedback>
  );
}

BuildUpdateNotice.propTypes = {
  monitor: PropTypes.shape({
    start: PropTypes.func.isRequired,
    stop: PropTypes.func.isRequired,
    getState: PropTypes.func.isRequired,
    subscribe: PropTypes.func.isRequired,
  }),
  gate: PropTypes.shape({
    getState: PropTypes.func.isRequired,
    subscribe: PropTypes.func.isRequired,
    reloadWhenSafe: PropTypes.func.isRequired,
  }),
  now: PropTypes.func,
  graceMs: PropTypes.number,
  t: PropTypes.func,
};

export default BuildUpdateNotice;
